import * as cheerio from 'cheerio';

/**
 * Reconstruct <colgroup> elements from colwidth attributes on table cells.
 *
 * The parser strips <colgroup> to keep the diff model clean, storing widths
 * as `colwidth` attrs. Browsers need actual <colgroup>/<col> for column layout.
 *
 * Only inspects the first row - tables with rowspan in row 1 may get an
 * incomplete colgroup.
 *
 * See also: backend `injectColgroupsForPrint.ts` in `htmlToPdf/` - nearly identical algorithm.
 */
export const injectColgroups = (html: string): string => {
  const $ = cheerio.load(html);

  $('table').each((_, tbl) => {
    const $tbl = $(tbl);
    if ($tbl.children('colgroup').length) return;

    const $firstRow = $tbl.find('> colgroup + tbody > tr, > tbody > tr, > tr').first();
    if (!$firstRow.length) return;

    const widths: Array<number | null> = [];
    $firstRow.children('th,td').each((_, cell) => {
      const $c = $(cell);
      const span = parseInt($c.attr('colspan') || '1', 10);
      const parts = ($c.attr('colwidth') || '')
        .split(',')
        .map((s) => parseInt(s, 10))
        .filter((n) => Number.isFinite(n));
      for (let i = 0; i < span; i++) widths.push(parts[i] ?? null);
    });

    if (!widths.some((w) => w != null)) return;

    const $cg = $('<colgroup/>');
    widths.forEach((w) => {
      const $col = $('<col/>');
      if (w != null && w > 0) $col.attr('style', `width: ${w}px;`);
      $cg.append($col);
    });
    $tbl.prepend($cg);
  });

  return $('body').html() ?? html;
};
